import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getAnalyticsCompaniesBySlugService } from 'services/analyticsCenter';
import {
  getCompaniesService,
  getLargeShareholderService,
  getLeaderListService,
  getOwnershipStructuresService,
} from 'services/analyticsCenter/businessProfile';
import {
  CompaniesTypes,
  LargeShareholderTypes,
  LeaderListParamsTypes,
  LeaderListTypes,
  OwnershipStructuresTypes,
  ParamsTypes,
} from 'services/analyticsCenter/businessProfile/types';
import { AnalyticCenterCompaniesTypes } from 'services/analyticsCenter/types';

type BusinessProfileState = {
  basicAndListedInfo?: CompaniesTypes[];
  companyInfoDetail?: AnalyticCenterCompaniesTypes;
  ownershipStructures: OwnershipStructuresTypes[];
  largeShareholder?: PaginationData<LargeShareholderTypes[]>;
  leaderList: LeaderListTypes[];
};

const initialState: BusinessProfileState = {
  basicAndListedInfo: undefined,
  companyInfoDetail: undefined,
  ownershipStructures: [],
  largeShareholder: undefined,
  leaderList: [],
};

export const getBasicAndListedInfoAction = createAsyncThunk<
  CompaniesTypes[],
  ParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'businessProfileReducer/getBasicAndListedInfoAction',
  async (params: ParamsTypes, { rejectWithValue }) => {
    try {
      return await getCompaniesService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getCompanyInfoDetailAction = createAsyncThunk<
  AnalyticCenterCompaniesTypes,
  string,
  { rejectValue: ErrorResponse }
>(
  'businessProfileReducer/getCompanyInfoDetailAction',
  async (stockSymbol: string, { rejectWithValue }) => {
    try {
      return await getAnalyticsCompaniesBySlugService(stockSymbol);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getLargeShareholderAction = createAsyncThunk<
  { data: PaginationData<LargeShareholderTypes[]>; isFirst?: boolean },
  ParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'businessProfileReducer/getLargeShareholder',
  async (params, { rejectWithValue }) => {
    try {
      const data = await getLargeShareholderService(params);
      return { data, isFirst: params.isFirst };
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getOwnershipStructuresAction = createAsyncThunk<
  OwnershipStructuresTypes[],
  ParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'businessProfileReducer/getOwnershipStructures',
  async (params: ParamsTypes, { rejectWithValue }) => {
    try {
      return await getOwnershipStructuresService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getLeaderListAction = createAsyncThunk(
  'businessProfileReducer/getLeaderListAction',
  async (params: LeaderListParamsTypes, { rejectWithValue }) => {
    try {
      return await getLeaderListService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const businessProfileSlice = createSlice({
  name: 'businessProfileReducer',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(getBasicAndListedInfoAction.fulfilled, ($state, action) => {
      $state.basicAndListedInfo = action.payload;
    });
    builder.addCase(getCompanyInfoDetailAction.fulfilled, ($state, action) => {
      $state.companyInfoDetail = action.payload;
    });
    builder.addCase(getLargeShareholderAction.fulfilled, ($state, action) => {
      $state.largeShareholder = !$state.largeShareholder || action.payload.isFirst
        ? action.payload.data
        : {
          ...action.payload.data,
          data: [
            ...$state.largeShareholder.data,
            ...action.payload.data.data,
          ],
        };
    });
    builder.addCase(
      getOwnershipStructuresAction.fulfilled,
      ($state, action) => {
        $state.ownershipStructures = action.payload;
      },
    );
    builder.addCase(getLeaderListAction.fulfilled, ($state, action) => {
      $state.leaderList = action.payload;
    });
  },
});

export default businessProfileSlice.reducer;
